import Dexie, { Transaction } from 'dexie';
import { db, Pref } from './db';
import { logger } from '../utils/logger';

const MODULE_LOADED_AT = Date.now();
const STARTUP_DELAY_MS = 30 * 1000;

db.on('populate', (tx: Transaction) => {
  // Use provided transaction to populate database with initial data
  tx.table('prefs').add({ name: 'backupEveryNSeconds', numVal: 60 });
});

export async function getBackupEveryNSeconds(): Promise<number> {
  const pref = await db.prefs.get('backupEveryNSeconds');
  if (pref == null || pref.numVal == null) {
    return 0;
  }
  return pref.numVal;
}

export async function getIsBackupOverdue(): Promise<boolean> {
  if (Date.now() - MODULE_LOADED_AT < STARTUP_DELAY_MS) {
    return false;
  }
  const lastBackup = await getLatestBackup();
  const interval = await getBackupEveryNSeconds();
  if (interval === 0 || interval == null) return false;
  return !lastBackup || (Date.now() - lastBackup) > interval * 1000;
}

export function setBackupEveryNSeconds(seconds: number) {
  return db.prefs.put({ name: 'backupEveryNSeconds', numVal: seconds });
}

export async function getBackupPath(): Promise<string | undefined> {
  return (await db.prefs.get('backupPath'))?.strVal;
}

export function setBackupPath(path: string) {
  return db.prefs.put({ name: 'backupPath', strVal: path });
}

export async function getBackupDirectoryHandle() {
  return (await db.prefs.get('backupDirectoryHandle'))?.dirHandle;
}

export async function setBackupDirectoryHandle(handle: FileSystemDirectoryHandle) {
  await db.prefs.put({ name: 'backupStatus', numVal: BackupStatus.DirectoryAccessible });
  return await db.prefs.put({ name: 'backupDirectoryHandle', dirHandle: handle });
}

export async function getBackupStatus() {
  const pref = await db.prefs.get('backupStatus');
  if (pref == null || pref.numVal == null) {
    return BackupStatus.DirectoryNotAccessible;
  }
  return pref.numVal as BackupStatus;
}

export function setBackupStatus(status: BackupStatus) {
  return db.prefs.put({ name: 'backupStatus', numVal: status });
}

export enum BackupStatus {
  Unknown, // Provide falsey "0" value
  // NoDirectorySelected, // effectively same as not accessible
  DirectoryNotAccessible,
  DirectoryAccessible,
  BackupInProgress,
  PermissionRevoked, // Permission was explicitly revoked; user must re-select the folder
}

export async function getLatestBackup() {
  const pref = await db.prefs.get('latestBackup');
  if (pref == null || pref.numVal == null) {
    return 0;
  }
  return pref.numVal;
}

export function setLatestBackup(timestamp: number) {
  return db.prefs.put({ name: 'latestBackup', numVal: timestamp });
}

export async function getLatestBackupSize() {
  const pref = await db.prefs.get('latestBackupSize');
  if (pref == null || pref.numVal == null) {
    return 0;
  }
  return pref.numVal;
}

export function setLatestBackupSize(size: number) {
  return db.prefs.put({ name: 'latestBackupSize', numVal: size });
}

export async function getLastFullBackup(): Promise<number> {
  const pref = await db.prefs.get('lastFullBackup');
  if (pref == null || pref.numVal == null) return 0;
  return pref.numVal;
}

export function setLastFullBackup(timestamp: number) {
  return db.prefs.put({ name: 'lastFullBackup', numVal: timestamp });
}

export async function getTabvanaWindowId(): Promise<number | undefined> {
  const x = await db.prefs.get('tabvanaWindowId');
  return x?.numVal;
}

export function setTabvanaWindowId(id: number | undefined) {
  return db.prefs.put({ name: 'tabvanaWindowId', numVal: id });
}



export function getTagsToProtectFromCleanup() {
  return ['Main'];
}



export async function getSimilarityScaler(): Promise<number> {
  const pref = await db.prefs.get('similarityScaler');
  if (pref == null || pref.numVal == null) {
    return 5;
  }
  return pref.numVal;
}

export function setSimilarityScaler(scale: number) {
  return db.prefs.put({ name: 'similarityScaler', numVal: scale });
}

export async function getTagSimilarityScaler(): Promise<number> {
  const pref = await db.prefs.get('tagSimilarityScaler');
  if (pref == null || pref.numVal == null) {
    return 20;
  }
  return pref.numVal;
}

export function setTagSimilarityScaler(scale: number) {
  return db.prefs.put({ name: 'tagSimilarityScaler', numVal: scale });
}

export async function getIgnoredUrls(): Promise<string[] | undefined> {
  const pref = await db.prefs.get('ignoredUrls');
  let parsed: string[] | undefined;
  const str = pref?.strVal;
  if (str) {
    try {
      parsed = JSON.parse(str);
    } catch (e) {
      logger.error('Failed to parse ignoredUrls', e);
    }
  }
  return parsed;
}

export function setIgnoredUrls(urls: string[]) {
  return db.prefs.put({ name: 'ignoredUrls', strVal: JSON.stringify(urls) });
}

export async function getNormalizeRules(): Promise<[string, string][] | undefined> {
  const pref = await db.prefs.get('normalizeRules');
  let parsed: [string, string][] | undefined;
  const str = pref?.strVal;
  if (str) {
    try {
      parsed = JSON.parse(str);
    } catch (e) {
      logger.error('Failed to parse normalizeRules', e);
    }
  }
  return parsed;
}

export function setNormalizeRules(rules: [string, string][]) {
  return db.prefs.put({ name: 'normalizeRules', strVal: JSON.stringify(rules) });
}
